import { ActionTree, GetterTree, MutationTree } from "vuex";
import { logError, logWarn } from "@/lib/utils/logger";

// エラーメッセージ定数
const ERROR_MESSAGES = {
  NO_NOVEL_ID: "選択できる原稿がありません。作品ページから原稿を選択してください。",
  API_ERROR: "原稿データの取得に失敗しました。",
  NETWORK_ERROR: "ネットワークエラーが発生しました。接続を確認してください。",
  AUTH_ERROR: "認証が必要です。再度ログインしてください。",
} as const;

// 原稿データの型定義
export interface ManuscriptData {
  id: string;
  title: string;
  content: string;
  characterCount: number;
  createdAt: string;
}

// State型定義（シンプル版 - lastApiCallを削除）
export interface AssistSelectManuscriptState {
  selectedNovelId: string | null;
  manuscriptData: ManuscriptData[] | null;
  selectedManuscripts: ManuscriptData[];
  isApiLoading: boolean;
  apiError: string | null;
}

// Mutations型定義（シンプル版 - lastApiCall関連削除）
interface Mutations {
  setSelectedNovelId(state: AssistSelectManuscriptState, novelId: string | null): void;
  setManuscriptData(state: AssistSelectManuscriptState, data: ManuscriptData[] | null): void;
  setSelectedManuscripts(state: AssistSelectManuscriptState, manuscripts: ManuscriptData[]): void;
  addSelectedManuscript(state: AssistSelectManuscriptState, manuscript: ManuscriptData): void;
  removeSelectedManuscript(state: AssistSelectManuscriptState, manuscriptId: string): void;
  setApiLoading(state: AssistSelectManuscriptState, loading: boolean): void;
  setApiError(state: AssistSelectManuscriptState, error: string | null): void;
  resetState(state: AssistSelectManuscriptState): void;
}

// Actions型定義（シンプル版 - 新規アクション追加、複雑機能削除）
interface Actions {
  // 🆕 Phase 2で設計した新規アクション
  initializeWithNovelId(context: any, novelId: string): Promise<void>;
  showNoManuscriptError(context: any): Promise<void>;
  resetState(context: any): Promise<void>;

  // 既存のシンプルアクション
  fetchManuscriptData(context: any, novelId: string): Promise<void>;
  selectManuscript(context: any, manuscript: ManuscriptData): Promise<void>;
  unselectManuscript(context: any, manuscriptId: string): Promise<void>;
  clearSelection(context: any): Promise<void>;

  // 🆕 Phase 5 追加：エラー管理アクション
  setApiError(context: any, error: string | null): Promise<void>;
  clearApiError(context: any): Promise<void>;
}

// Getters型定義（シンプル版 - lastApiCall削除）
interface Getters {
  selectedNovelId(state: AssistSelectManuscriptState): string | null;
  manuscriptData(state: AssistSelectManuscriptState): ManuscriptData[] | null;
  selectedManuscripts(state: AssistSelectManuscriptState): ManuscriptData[];
  selectedManuscriptIds(state: AssistSelectManuscriptState): string[];
  isManuscriptSelected(state: AssistSelectManuscriptState): (manuscriptId: string) => boolean;
  selectedManuscriptCount(state: AssistSelectManuscriptState): number;
  isApiLoading(state: AssistSelectManuscriptState): boolean;
  apiError(state: AssistSelectManuscriptState): string | null;
  hasManuscriptData(state: AssistSelectManuscriptState): boolean;
}

// Mutations（シンプル版 - lastApiCall関連削除）
const mutations: MutationTree<AssistSelectManuscriptState> & Mutations = {
  setSelectedNovelId(state, novelId) {
    state.selectedNovelId = novelId;
  },
  setManuscriptData(state, data) {
    state.manuscriptData = data;
  },
  setSelectedManuscripts(state, manuscripts) {
    state.selectedManuscripts = manuscripts;
  },
  addSelectedManuscript(state, manuscript) {
    const exists = state.selectedManuscripts.find((m) => m.id === manuscript.id);
    if (!exists) {
      state.selectedManuscripts.push(manuscript);
    }
  },
  removeSelectedManuscript(state, manuscriptId) {
    state.selectedManuscripts = state.selectedManuscripts.filter((m) => m.id !== manuscriptId);
  },
  setApiLoading(state, loading) {
    state.isApiLoading = loading;
  },
  setApiError(state, error) {
    state.apiError = error;
  },
  resetState(state) {
    state.selectedNovelId = null;
    state.manuscriptData = null;
    state.selectedManuscripts = [];
    state.isApiLoading = false;
    state.apiError = null;
  },
};

// Actions（シンプル版 - 新規アクション追加、複雑機能削除）
const actions: ActionTree<AssistSelectManuscriptState, {}> & Actions = {
  // 🆕 1. Novel IDによる初期化（Phase 2設計）
  async initializeWithNovelId({ commit, dispatch }, novelId: string) {
    try {
      // 1. 前状態をシンプルにクリア
      commit("resetState");

      // 2. 新しいNovelIDを設定
      commit("setSelectedNovelId", novelId);

      // 3. 原稿データを取得
      await dispatch("fetchManuscriptData", novelId);
    } catch (error) {
      logError({ module: "assistSelectManuscript", action: "initializeWithNovelId" }, "初期化エラー", error);
      commit("setApiError", ERROR_MESSAGES.API_ERROR);
    }
  },

  // 🆕 2. 原稿選択不可エラー表示（Phase 2設計）
  async showNoManuscriptError({ commit }) {
    commit("resetState");
    commit("setApiError", ERROR_MESSAGES.NO_NOVEL_ID);
  },

  // 🆕 3. 完全状態リセット（Phase 2設計）
  async resetState({ commit }) {
    commit("resetState");
  },

  // 既存アクション（改善版 + DisplayOrder連携）
  async fetchManuscriptData({ commit }, novelId) {
    try {
      commit("setApiLoading", true);
      commit("setApiError", null);

      // 🆕 実際のAPIクライアントをインポートして使用
      const { ManuscriptClient, DisplayOrderClient } = await import("@/lib/clients");
      const { SharedLinkKind } = await import("@/lib/models");

      const manuscriptClient = new ManuscriptClient();
      const displayOrderClient = new DisplayOrderClient();

      // 実際のAPI呼び出し：原稿一覧を取得
      const apiManuscripts = await manuscriptClient.fetchAllManuscript(novelId);

      if (!apiManuscripts || apiManuscripts.length === 0) {
        commit("setManuscriptData", []);
        return;
      }

      // 各原稿の詳細情報を並列取得
      const manuscriptPromises = apiManuscripts.map(async (manuscript, index) => {
        const manuscriptKey = manuscript.key;
        let characterCount = 0;
        let updatedAt = new Date();

        try {
          // バージョン情報を取得
          if (manuscriptKey) {
            const versions = await manuscriptClient.fetchManuscriptVersions(novelId, manuscriptKey);

            // 最新バージョンの情報を取得
            const latestVersion = versions.find((v) => v.isLatest);
            if (latestVersion) {
              updatedAt = new Date(latestVersion.lastModified);

              // 原稿内容を取得して文字数をカウント
              const content = await manuscriptClient.fetchManuscriptContent(
                novelId,
                manuscriptKey,
                latestVersion.versionId
              );
              if (content) {
                characterCount = content.length;
              }
            }
          }
        } catch (detailError) {
          logWarn(
            { module: "assistSelectManuscript", action: "fetchManuscriptDetails" },
            `原稿 ${manuscriptKey} の詳細情報取得に失敗`,
            detailError
          );
          // 詳細情報の取得に失敗してもリストには含める
        }

        return {
          id: manuscriptKey || `manuscript-${index + 1}`,
          title: manuscript.title || "(タイトル未設定)",
          content: "", // 内容は保存しない（文字数のみ）
          characterCount,
          createdAt: updatedAt.toISOString(),
        };
      });

      const processedManuscriptData = await Promise.all(manuscriptPromises);

      // 🆕 DisplayOrder連携：メイン原稿リストと同じソート処理を適用
      const displayOrder = await displayOrderClient.fetchDisplayOrder({
        novelId,
        kind: SharedLinkKind.MANUSCRIPT,
      });

      let sortedManuscriptData: ManuscriptData[];

      if (displayOrder.order.length === 0) {
        // カスタム順序が未設定の場合：API取得順序のまま
        sortedManuscriptData = processedManuscriptData;
      } else {
        // DisplayOrderに従ってソート（メイン原稿リストと同じロジック）
        const orderedManuscripts = processedManuscriptData
          .filter((m) => displayOrder.order.includes(m.id))
          .sort((a, b) => displayOrder.order.indexOf(a.id) - displayOrder.order.indexOf(b.id));

        const unorderedManuscripts = processedManuscriptData.filter((m) => !displayOrder.order.includes(m.id));

        // 順序設定済み原稿 + 未設定原稿の順で結合
        sortedManuscriptData = [...orderedManuscripts, ...unorderedManuscripts];
      }

      commit("setManuscriptData", sortedManuscriptData);
    } catch (error) {
      logError({ module: "assistSelectManuscript", action: "fetchManuscriptData" }, "API呼び出しでエラー", error);
      commit("setApiError", ERROR_MESSAGES.API_ERROR);
      commit("setManuscriptData", null);
    } finally {
      commit("setApiLoading", false);
    }
  },

  async selectManuscript({ commit }, manuscript) {
    commit("addSelectedManuscript", manuscript);
  },

  async unselectManuscript({ commit }, manuscriptId) {
    commit("removeSelectedManuscript", manuscriptId);
  },

  async clearSelection({ commit }) {
    commit("setSelectedManuscripts", []);
  },

  // 🆕 Phase 5 追加：エラー管理アクション
  async setApiError({ commit }, error) {
    commit("setApiError", error);
  },

  async clearApiError({ commit }) {
    commit("setApiError", null);
  },
};

// Getters（シンプル版 - lastApiCall削除）
const getters: GetterTree<AssistSelectManuscriptState, {}> & Getters = {
  selectedNovelId: (state) => state.selectedNovelId,
  manuscriptData: (state) => state.manuscriptData,
  selectedManuscripts: (state) => state.selectedManuscripts,
  selectedManuscriptIds: (state) => state.selectedManuscripts.map((m) => m.id),
  isManuscriptSelected: (state) => (manuscriptId: string) =>
    state.selectedManuscripts.some((m) => m.id === manuscriptId),
  selectedManuscriptCount: (state) => state.selectedManuscripts.length,
  isApiLoading: (state) => state.isApiLoading,
  apiError: (state) => state.apiError,
  hasManuscriptData: (state) => !!(state.manuscriptData && state.manuscriptData.length > 0),
};

// 初期状態（シンプル版 - lastApiCall削除）
const state: AssistSelectManuscriptState = {
  selectedNovelId: null,
  manuscriptData: null,
  selectedManuscripts: [],
  isApiLoading: false,
  apiError: null,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export { ERROR_MESSAGES };
